import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/useAuth';
import { friendlyErrorMessage } from '../lib/errors';
import { Button } from '../components/Button';
import { TextField } from '../components/Field';
import { lastEmail, lastPath, rememberEmail } from '../lib/keepSignedIn';

// Sign-in for admins. The default is an emailed sign-in link + code
// (Supabase OTP; the hosted project sends 8 digits), because many Calyard accounts were created with
// Sign in with Apple and have no password. `shouldCreateUser: false`:
// this page never creates accounts. Password sign-in stays available
// behind "Use a password instead".
type Step = 'email' | 'code' | 'password';

export function LoginPage() {
  const { session, loading } = useAuth();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState(lastEmail);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  if (!loading && session) return <Navigate to={lastPath()} replace />;

  async function sendLink(e?: FormEvent) {
    e?.preventDefault();
    if (!email) {
      setError('Enter your email first.');
      return;
    }
    rememberEmail(email);
    setBusy(true);
    setError(null);
    setNotice(null);
    const { error: otpError } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { shouldCreateUser: false, emailRedirectTo: `${window.location.origin}${lastPath()}` },
    });
    setBusy(false);
    if (otpError) {
      setError(friendlyErrorMessage(otpError));
      return;
    }
    setStep('code');
  }

  async function verifyCode(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error: verifyError } = await supabase.auth.verifyOtp({ email: email.trim(), token: code.trim(), type: 'email' });
    setBusy(false);
    if (verifyError) setError(friendlyErrorMessage(verifyError));
  }

  async function signInWithPassword(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    rememberEmail(email);
    const { error: signInError } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    setBusy(false);
    if (signInError) setError(friendlyErrorMessage(signInError));
  }

  async function forgotPassword() {
    if (!email) {
      setError('Enter your email first, then tap "Forgot password".');
      return;
    }
    setBusy(true);
    setError(null);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setBusy(false);
    if (resetError) setError(friendlyErrorMessage(resetError));
    else setNotice('Check your email for a reset link.');
  }

  const linkStyle = { background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 } as const;

  return (
    <div className="center-screen">
      <form className="card auth-card" onSubmit={step === 'email' ? sendLink : step === 'code' ? verifyCode : signInWithPassword}>
        <h1 style={{ fontSize: 28, marginBottom: 8 }}>Calyard admin</h1>

        {step === 'code' ? (
          <>
            <p className="muted" style={{ marginBottom: 20 }}>
              We emailed a sign-in link and a code to <strong>{email.trim()}</strong>. Open the link on this computer, or type the code here.
            </p>
            <TextField
              label="Code"
              id="login-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 10))}
            />
            {error ? <p className="error" style={{ marginBottom: 12 }}>{error}</p> : null}
            <Button type="submit" disabled={busy || code.length < 6} style={{ width: '100%', marginBottom: 12 }}>
              {busy ? 'Checking…' : 'Sign in'}
            </Button>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <button type="button" className="muted" style={linkStyle} onClick={() => void sendLink()} disabled={busy}>
                Send a new code
              </button>
              <button type="button" className="muted" style={linkStyle} onClick={() => { setStep('email'); setCode(''); setError(null); }}>
                Use another email
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="muted" style={{ marginBottom: 20 }}>
              Sign in with the email of your Calyard app account (including accounts made with Sign in with Apple). Admin access is granted separately.
            </p>
            <TextField
              label="Email"
              id="login-email"
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {step === 'password' ? (
              <TextField
                label="Password"
                id="login-password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            ) : null}
            {error ? <p className="error" style={{ marginBottom: 12 }}>{error}</p> : null}
            {notice ? <p className="muted" style={{ marginBottom: 12 }}>{notice}</p> : null}
            <Button type="submit" disabled={busy} style={{ width: '100%', marginBottom: 12 }}>
              {busy ? 'One moment…' : step === 'password' ? 'Sign in' : 'Email me a sign-in link'}
            </Button>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              {step === 'password' ? (
                <>
                  <button type="button" className="muted" style={linkStyle} onClick={() => { setStep('email'); setError(null); }}>
                    Email me a link instead
                  </button>
                  <button type="button" className="muted" style={linkStyle} onClick={forgotPassword}>
                    Forgot password?
                  </button>
                </>
              ) : (
                <button type="button" className="muted" style={linkStyle} onClick={() => { setStep('password'); setError(null); }}>
                  Use a password instead
                </button>
              )}
            </div>
          </>
        )}
      </form>
    </div>
  );
}
